import type { AccountLogoReplacementFailedAction } from '@Application/Branding/AccountLogo/Actions/accountLogoReplacementFailed';
import ReplaceAccountLogo from '@Application/Branding/AccountLogo/Commands/ReplaceAccountLogo';
import FetchAccountLogo from '@Application/Branding/AccountLogo/Queries/FetchAccountLogo';
import fetchAccountLogo from '@Application/Branding/AccountLogo/fetchAccountLogo';
import replaceAccountLogo from '@Application/Branding/AccountLogo/replaceAccountLogo';
import type { OrganizationLogoReplacementFailedAction } from '@Application/Branding/OrganizationLogo/Actions/organizationLogoReplacementFailed';
import ReplaceOrganizationLogo from '@Application/Branding/OrganizationLogo/Commands/ReplaceOrganizationLogo';
import FetchOrganizationLogo from '@Application/Branding/OrganizationLogo/Queries/FetchOrganizationLogo';
import fetchOrganizationLogo from '@Application/Branding/OrganizationLogo/fetchOrganizationLogo';
import replaceOrganizationLogo from '@Application/Branding/OrganizationLogo/replaceOrganizationLogo';
import { theme } from '@Guidelines/Theme/theme';
import LogoPreview from '@UI/Pages/Administration/Organizations/Shared/LogoPreview';
import logoSidebarSignal, {
	closeLogoSidebar,
} from '@UI/Pages/Administration/Organizations/Signals/logoSidebarSignal';
import { requestLogoRemoval } from '@UI/Pages/Administration/Organizations/Signals/logoToRemoveSignal';
import { LOGO_ACCEPTED_TYPES } from '@UI/Pages/Administration/Organizations/Sidebars/LogoSidebar/FormData/constraint/logoConstraints';
import LogoFormData from '@UI/Pages/Administration/Organizations/Sidebars/LogoSidebar/FormData/LogoFormData';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import { css } from '@emotion/react';
import * as React from 'react';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector, useStoreQuery } from '@verclam/synapse';
import { Button, Form, Sidebar, Skeleton } from '@verclam/ui-kit';

const hiddenInputStyle = css`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`;

const sectionStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	margin-bottom: 1.25rem;
`;

const fileRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex-wrap: wrap;
`;

const mutedStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const EMPTY_ACCOUNT_MESSAGE = "Aucun logo : le logo de l'application est affiché.";
const EMPTY_ORGANIZATION_MESSAGE =
	"Aucun logo propre : le logo du compte, à défaut celui de l'application, est affiché.";

const formatFileSize = (bytes: number): string => {
	if (bytes < 1024) {
		return `${bytes} o`;
	}

	return `${(bytes / 1024).toFixed(1).replace('.', ',')} Ko`;
};

/**
 * Deposit panel for the account logo or an organization's own logo, told apart by the signal's
 * `owner`. The previewed logo is the owner's own one, never the inherited one.
 */
const LogoSidebar: React.FC = () => {
	const dispatch = useDispatch();
	const target = useSignalValue(logoSidebarSignal);
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;
	const organizations = useSelector((state) => state.organizationsStore.organizations);

	const [formData, setFormData] = React.useState(() => LogoFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const formId = React.useId();
	const fileInputId = React.useId();
	const fileInputRef = React.useRef<HTMLInputElement>(null);

	const isAccountTarget = target?.owner === 'account';
	const organizationId = target?.owner === 'organization' ? target.organizationId : null;

	const { data: accountLogo, error: accountLogoError } = useStoreQuery({
		selector: (state) => state.brandingStore.accountLogo,
		stopOn: (value) => !isAccountTarget || (value !== null && value.ownerId === accountId),
		query: () => {
			if (!isAccountTarget || accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchAccountLogo(new FetchAccountLogo(accountId)));
		},
		deps: [accountId, isAccountTarget],
	});

	const { data: organizationLogo, error: organizationLogoError } = useStoreQuery({
		selector: (state) => state.brandingStore.organizationLogo,
		stopOn: (value) => organizationId === null || (value !== null && value.ownerId === organizationId),
		query: () => {
			if (organizationId === null || accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchOrganizationLogo(new FetchOrganizationLogo(organizationId)));
		},
		deps: [accountId, organizationId],
	});

	React.useEffect(() => {
		if (target !== null) {
			setFormData(LogoFormData.createEmpty());
		}
	}, [target]);

	const organizationName = React.useMemo(() => {
		if (organizationId === null || !organizations) {
			return '';
		}

		return organizations.findById(organizationId)?.name ?? '';
	}, [organizationId, organizations]);

	const currentLogo = isAccountTarget ? accountLogo : organizationLogo;
	const currentOwnerId = isAccountTarget ? accountId : organizationId;
	const currentError = isAccountTarget ? accountLogoError : organizationLogoError;
	const isCurrentLogoLoaded =
		currentLogo !== null && currentOwnerId !== null && currentLogo.ownerId === currentOwnerId;
	const hasOwnLogo = isCurrentLogoLoaded && currentLogo.hasImage;

	const handleBrowseClick = React.useCallback(() => {
		fileInputRef.current?.click();
	}, []);

	const handleFileInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0] ?? null;

		// Cleared so the same file can be re-selected after a rejection.
		event.target.value = '';
		setFormData((current) => current.setFile(file));
	}, []);

	const handleRemoveClick = React.useCallback(() => {
		if (target !== null) {
			requestLogoRemoval(target);
		}
	}, [target]);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		const file = formData.file;

		// Unreachable in practice: validity implies a file, and `AppShell` never mounts this page without an account.
		if (file === null || accountId === null || target === null) {
			return;
		}

		setIsSubmitting(true);

		if (target.owner === 'account') {
			dispatch(replaceAccountLogo(new ReplaceAccountLogo(accountId, file)))
				.then(() => closeLogoSidebar())
				.catch((action: AccountLogoReplacementFailedAction) =>
					updateValidationFromExternalSource({
						action,
						eventNames: 'ACCOUNT_LOGO_REPLACEMENT_FAILED',
						formDataUpdater: setFormData,
					}),
				)
				.finally(() => setIsSubmitting(false));
			return;
		}

		dispatch(replaceOrganizationLogo(new ReplaceOrganizationLogo(target.organizationId, file)))
			.then(() => closeLogoSidebar())
			.catch((action: OrganizationLogoReplacementFailedAction) =>
				updateValidationFromExternalSource({
					action,
					eventNames: 'ORGANIZATION_LOGO_REPLACEMENT_FAILED',
					formDataUpdater: setFormData,
				}),
			)
			.finally(() => setIsSubmitting(false));
	}, [formData, accountId, target, dispatch]);

	const renderCurrentLogo = (): React.ReactNode => {
		if (currentError !== null) {
			return <p css={mutedStyle}>Le logo actuel n'a pas pu être chargé.</p>;
		}

		if (!isCurrentLogoLoaded) {
			return <Skeleton width="15rem" height="4rem" />;
		}

		if (currentLogo.dataUrl !== null) {
			return (
				<LogoPreview
					dataUrl={currentLogo.dataUrl}
					alt={isAccountTarget ? 'Logo du compte' : `Logo de la structure ${organizationName}`}
				/>
			);
		}

		return <p css={mutedStyle}>{isAccountTarget ? EMPTY_ACCOUNT_MESSAGE : EMPTY_ORGANIZATION_MESSAGE}</p>;
	};

	const selectedFile = formData.file;
	const fileErrorMessage = formData.getValidationMessage('file') ?? undefined;

	return (
		<Sidebar
			visible={target !== null}
			onHide={closeLogoSidebar}
			position="right"
			header={isAccountTarget ? 'Logo du compte' : `Logo de la structure ${organizationName}`}
			footer={
				<React.Fragment>
					<Button type="button" severity="secondary" label="Annuler" onClick={closeLogoSidebar} />
					<Button type="submit" form={formId} label="Enregistrer" loading={isSubmitting} />
				</React.Fragment>
			}
			blockScroll
		>
			<div css={sectionStyle}>
				{renderCurrentLogo()}
				{hasOwnLogo ? (
					<div css={fileRowStyle}>
						<Button
							type="button"
							severity="danger"
							outlined
							label="Retirer le logo"
							onClick={handleRemoveClick}
						/>
					</div>
				) : null}
			</div>
			<Form id={formId} onSubmit={handleSubmit} aria-label="Déposer un logo">
				<div css={sectionStyle}>
					<div css={fileRowStyle}>
						<Button type="button" label="Choisir une image" onClick={handleBrowseClick} />
						{selectedFile !== null ? (
							<span>
								{selectedFile.name} ({formatFileSize(selectedFile.size)})
							</span>
						) : null}
					</div>
					<label css={hiddenInputStyle} htmlFor={fileInputId}>
						Choisir une image
					</label>
					<input
						ref={fileInputRef}
						id={fileInputId}
						css={hiddenInputStyle}
						type="file"
						accept={LOGO_ACCEPTED_TYPES.join(',')}
						onChange={handleFileInputChange}
					/>
					<p css={mutedStyle}>
						PNG, JPEG ou WebP · 512 Ko maximum · entre 32 et 2 048 pixels de côté.
					</p>
					{fileErrorMessage !== undefined ? <small>{fileErrorMessage}</small> : null}
				</div>
			</Form>
		</Sidebar>
	);
};

export default LogoSidebar;
